import type { CustomCategory, CustomCategoryCustomFields } from '../types/customCategory';
import { getCategoryUrl, getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { Vendor } from '../types/vendor';
import type { Schemas } from '@shopware/api-client/api-types';
import { MinimalCategoryIncludes } from '../utils/includes';

export function useVendor() {
    const { apiClient } = useShopwareContext();
    const { getFormattedPrice } = usePrice();
    const { countryId, sessionContext } = useSessionContext();

    const getVendorById = async (vendorId: string) => {
        return useAsyncData(`vendor-${vendorId}`, async () => {
            try {
                return (
                    await apiClient.invoke('getVendor post /vendor/search', {
                        body: {
                            ids: vendorId,
                            page: 1,
                            associations: {
                                products: {
                                    limit: 8,
                                    associations: {
                                        prices: {},
                                        vendorsAvailableSalesChannels: {
                                            "total-count-mode": 1
                                        },
                                    },
                                    filter: [
                                        {
                                            field: 'vendorsAvailableSalesChannels.id',
                                            type: 'equals',
                                            value: sessionContext.value?.salesChannel?.id || '',
                                        },

                                    ],
                                    "total-count-mode": 1
                                },
                                shippingMethod: {
                                    "total-count-mode": 1,
                                    associations: {
                                        prices: {
                                            "total-count-mode": 1
                                        }
                                    }
                                },
                            },
                            "total-count-mode": 1
                        },
                    })
                ).data;
            } catch (error) {
                return null;
            }
        });
    };

    const getVendor = async (category: Ref<CustomCategory> | undefined) => {
        if (!category) {
            return null;
        }

        const customFields = getTranslatedProperty(
            category.value,
            'customFields',
        ) as unknown as CustomCategoryCustomFields;
        const vendorId = customFields.vendor_id;

        if (!vendorId) {
            return null;
        }

        const { data: vendor } = await getVendorById(vendorId);
        return vendor.value as Vendor | null;
    };

    const getVendorProducts = async (products: Schemas['Product'][] | null | undefined) => {
        if (!products) {
            return null;
        }

        const productIds = products.map((product: Schemas['Product']) => product.id);
        try {
            const { data } = await apiClient.invoke('getProducts post /product', {
                body: {
                    ids: productIds,

                    associations: {
                        children: {},
                        seoUrls: {},
                        vendorsAvailableSalesChannels: {},
                    },
                },
            });

            return data?.elements;
        } catch (error) {
            return null;
        }
    };

    const getCalculatedShippingCosts = (shippingMethod: Schemas['ShippingMethod'] | undefined | null) => {
        if (!shippingMethod || !shippingMethod.prices?.length) {
            return null;
        }

        // no range -> we return the price
        if (shippingMethod.prices?.length === 1) {
            return getFormattedPrice(shippingMethod.prices?.pop()?.currencyPrice?.pop()?.gross);
        }

        const sortedPrices = getSortedShippingCosts(shippingMethod.prices);
        if (!sortedPrices || sortedPrices.length === 0) {
            return null;
        }

        return `${getFormattedPrice(sortedPrices[0]?.gross)} - ${getFormattedPrice(sortedPrices.pop()?.gross)}`;
    };

    const getShippingCosts = (vendor: Vendor | null) => {
        // in the context of vendor the minimum order value describes the value where the customer
        // can order something without paying the shipping costs
        if (!vendor) {
            return null;
        }

        const minimumOrderValue: Ref<null | string> = ref(null);
        vendor.shippingMethod?.prices?.forEach((price: Schemas['ShippingMethodPrice']) => {
            if (price.currencyPrice?.[0]?.gross === 0) {
                minimumOrderValue.value = getFormattedPrice(price.quantityStart);
                return;
            }
        });

        return {
            minimumOrderValue: minimumOrderValue.value,
            shippingCosts: getCalculatedShippingCosts(vendor.shippingMethod),
        };
    };

    const getSortedShippingCosts = (shippingMethodPrices: Schemas['ShippingMethodPrice'][]) => {
        const prices = shippingMethodPrices
            ?.map((price: Schemas['ShippingMethodPrice']) => price.currencyPrice?.[0])
            .filter(item => item !== undefined) as Schemas['Price'][];
        if (!prices || prices.length === 0) {
            return null;
        }

        // sort prices from smallest to largest
        return prices.sort((priceA: Schemas['Price'], priceB: Schemas['Price']) => {
            if (priceA?.gross < priceB?.gross) return -1;
            if (priceA?.gross > priceB?.gross) return 1;

            return priceA?.net - priceB?.net;
        });
    };

    const getFirstChildrenUrl = async (categoryId: string | undefined) => {
        if (!categoryId) {
            return null;
        }

        const { data } = await useAsyncData(`category-${categoryId}`, async () => {
            try {
                return (
                    await apiClient.invoke('getCategory post /category', {
                        body: {
                            filter: [
                                {
                                    field: 'parentId',
                                    type: 'equals',
                                    value: categoryId,
                                },
                            ],
                            associations: {
                                children: {},
                                seoUrls: {},
                            },
                            limit: 1,
                        },
                    })
                ).data;
            } catch (error) {
                return null;
            }
        });

        // we only want the first one (we also fetch only one)
        return data.value.elements[0] ?? null;
    };

    const getCategory = async (vendorId: string | undefined) => {
        if (!vendorId) {
            return null;
        }

        const { data } = await useAsyncData(`category-${vendorId}`, async () => {
            try {
                return (
                    await apiClient.invoke('getCategory post /category', {
                        body: {
                            filter: [
                                {
                                    field: 'customFields',
                                    type: 'contains',
                                    value: vendorId,
                                },
                            ],
                            associations: {
                                seoUrls: {},
                            },
                            includes: {
                                category: MinimalCategoryIncludes,
                            },
                            limit: 1,
                        },
                    })
                ).data;
            } catch (error) {
                return null;
            }
        });

        return data.value?.elements?.at(0);
    };

    const getCategoryLink = async (vendorId: string | undefined) => {
        const category = await getCategory(vendorId);
        return category ? getCategoryUrl(category) : null;
    };

    const getCountrySpecificVendor = (vendors: Vendor[] | undefined) => {
        return vendors?.find((vendor: Vendor) => vendor.countryId === countryId.value);
    };

    return {
        getVendor,
        getVendorProducts,
        getShippingCosts,
        getFirstChildrenUrl,
        getCategoryLink,
        getCountrySpecificVendor,
    };
}
